import { useState } from 'react';
import { useParams, Link } from '@tanstack/react-router';
import { ArrowLeft, Edit2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { DealForm } from '@/components/pipeline/deal-form';
import { formatCurrency, formatDate, formatRelative } from '@/lib/utils';
import { DEAL_STAGE_COLORS } from '@/lib/constants';
import { trpc } from '@/trpc';
import { usePipelineStore } from '@/stores/pipeline.store';

export function DealDetailPage() {
  const { dealId } = useParams({ strict: false }) as { dealId: string };
  const [editing, setEditing] = useState(false);

  // Try live API first, fall back to pipeline store
  const { data: apiDeal, isLoading } = (trpc as any).deal?.getById?.useQuery?.(
    { id: dealId },
    { retry: false },
  ) ?? { data: undefined, isLoading: false };

  const storeDeal = usePipelineStore((s) => s.deals.find((d) => d.id === dealId));
  const deal = apiDeal ?? storeDeal ?? null;

  if (isLoading && !storeDeal) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!deal) {
    return (
      <div className="space-y-6">
        <Link to="/deals" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="mr-1 h-4 w-4" /> Back to deals
        </Link>
        <div className="py-12 text-center text-muted-foreground">Deal not found.</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Link to="/deals" className="text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold tracking-tight">{deal.title}</h1>
          <p className="text-muted-foreground">{deal.companyName}</p>
        </div>
        <Button
          variant={editing ? 'ghost' : 'outline'}
          size="sm"
          onClick={() => setEditing(!editing)}
        >
          {editing ? (
            <>
              <X className="mr-1 h-4 w-4" /> Cancel
            </>
          ) : (
            <>
              <Edit2 className="mr-1 h-4 w-4" /> Edit
            </>
          )}
        </Button>
      </div>

      {editing ? (
        <DealForm
          deal={deal}
          onSave={() => setEditing(false)}
          onCancel={() => setEditing(false)}
        />
      ) : (
        <div className="grid gap-6 md:grid-cols-3">
          <div className="md:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Deal Details</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-4 sm:grid-cols-2">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Stage</p>
                  <Badge
                    variant="secondary"
                    className="mt-1"
                    style={{
                      backgroundColor: DEAL_STAGE_COLORS[deal.stage] + '20',
                      color: DEAL_STAGE_COLORS[deal.stage],
                    }}
                  >
                    {deal.stage}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Amount</p>
                  <p className="mt-1 text-lg font-semibold">
                    {deal.amount
                      ? formatCurrency(deal.amount, deal.currency ?? 'CAD')
                      : 'Not set'}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Currency</p>
                  <p className="mt-1 text-sm">
                    {deal.fxLockedAt
                      ? `${deal.currency} — Rate locked at ${deal.fxRate?.toFixed(4)} on ${formatDate(deal.fxLockedAt)}`
                      : deal.currency && deal.currency !== 'CAD'
                        ? `${deal.currency} → CAD (live rate)`
                        : (deal.currency ?? 'CAD')}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Expected Close</p>
                  <p className="mt-1">
                    {deal.expectedClose ? formatDate(deal.expectedClose) : 'Not set'}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Probability</p>
                  <p className="mt-1">{deal.probability != null ? `${deal.probability}%` : '-'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Contact</p>
                  <p className="mt-1">{deal.contactName ?? '-'}</p>
                  <p className="text-sm text-muted-foreground">{deal.contactEmail ?? ''}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Source</p>
                  <p className="mt-1">{deal.source ?? '-'}</p>
                </div>
                {deal.notes && (
                  <div className="sm:col-span-2">
                    <p className="text-sm font-medium text-muted-foreground">Notes</p>
                    <p className="mt-1 text-sm whitespace-pre-wrap">{deal.notes}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Activity</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center py-8 text-sm text-muted-foreground">
                  No activity yet.
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Timeline</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Created</span>
                  <span>{formatDate(deal.createdAt)}</span>
                </div>
                <Separator />
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Updated</span>
                  <span>{formatRelative(deal.updatedAt)}</span>
                </div>
                {deal.actualClose && (
                  <>
                    <Separator />
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Closed</span>
                      <span>{formatDate(deal.actualClose)}</span>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {deal.nextStep && (
              <Card>
                <CardHeader>
                  <CardTitle>Next Step</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm">{deal.nextStep}</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
